import * as z from 'zod/mini';
import { CABLE_ID_MAX_LENGTH, CABLE_ID_PAD, CABLE_ID_PREFIX } from './constants.ts';

// A run's cable ID (ROADMAP I7): one ID per run, unique inside its property,
// referenced everywhere the run appears — the run page, the link, the panel
// schedule, the report, and the printed labels. A person may type their own
// scheme (a jack number, a drawing reference); left blank, the server fills
// it from a running "CBL-0001" sequence so every run has an ID from the start.

/** A cable ID as stored: trimmed and bounded; '' means "generate one for me". */
export const cableIdSchema = z.string().check(z.trim(), z.maxLength(CABLE_ID_MAX_LENGTH));

/** "CBL-", the head of a generated ID; a typed ID may be anything else. */
const GENERATED_PREFIX = `${CABLE_ID_PREFIX}-`;

/** Matches a generated ID and captures its number: "CBL-0007" → 7. */
const GENERATED = new RegExp(String.raw`^${CABLE_ID_PREFIX}-(?<number>\d+)$`, 'u');

/** The number a generated ID carries, or 0 when the ID was typed by a person. */
function generatedNumber(cableId: string): number {
  const match = GENERATED.exec(cableId.trim());
  return match?.groups?.['number'] === undefined ? 0 : Number(match.groups['number']);
}

/**
 * The next cable ID for a property, given the IDs already in it: one past the
 * highest "CBL-N" in use, padded to CABLE_ID_PAD digits. Typed IDs that do not
 * follow the scheme are skipped, so a site that numbers its own runs still
 * gets a clean next number when one is generated. The result is guaranteed not
 * to collide with any generated ID already present.
 */
export function nextCableId(existing: readonly string[]): string {
  let highest = 0;
  for (const id of existing) {
    highest = Math.max(highest, generatedNumber(id));
  }
  return `${GENERATED_PREFIX}${String(highest + 1).padStart(CABLE_ID_PAD, '0')}`;
}

/**
 * The cable ID to store for a run: the one given if it is not blank, otherwise
 * a fresh generated one that does not clash with `existing`.
 */
export function resolveCableId(given: string, existing: readonly string[]): string {
  const trimmed = given.trim();
  return trimmed === '' ? nextCableId(existing) : trimmed;
}
